import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn, formatCount } from '../lib/utils.js';
import { Button } from './button.js';

/**
 * cursor ページングの操作 UI（ARCHITECTURE.md §4.3）。
 *
 * API の既定は cursor で、**総件数を返さない**。したがってこの UI には
 * 「全 N 件中 x〜y 件」も「N ページ目」も出せない。出せないものを
 * 無理に出そうとして COUNT(*) を走らせると、件数が伸びたときに一覧が
 * 丸ごと遅くなるので、前へ / 次へ + 表示件数だけに割り切る。
 *
 * 総件数が業務上必要な箇所（ダッシュボードの StatCard、プラン残数、
 * 請求明細）は専用の集計エンドポイントで別途取得すること。
 */

/** 表示件数の選択肢。API 側の上限は 200（§4.3）。 */
export const PAGE_SIZE_OPTIONS = [20, 50, 100, 200] as const;

export interface CursorPaginationProps {
  /** 次ページのカーソル。null なら次が無い。 */
  nextCursor: string | null;
  /** 「前へ」で戻る先。呼び出し側がカーソルのスタックを持つ。 */
  canGoPrev: boolean;
  onNext: () => void;
  onPrev: () => void;
  limit: number;
  onLimitChange?: (limit: number) => void;
  /** 現在ページの件数。「表示中 N 件」の表示に使う。 */
  currentCount?: number;
  loading?: boolean;
  className?: string;
}

export function CursorPagination({
  nextCursor,
  canGoPrev,
  onNext,
  onPrev,
  limit,
  onLimitChange,
  currentCount,
  loading = false,
  className,
}: CursorPaginationProps) {
  const selectId = React.useId();

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-panel px-3 py-2',
        className,
      )}
    >
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {onLimitChange ? (
          <label className="flex items-center gap-1.5" htmlFor={selectId}>
            <span>表示件数</span>
            <select
              id={selectId}
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="h-7 rounded-md border border-border bg-panel-elevated px-1.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {/* 数値の整形は `formatCount()` 1 か所に寄せる（ロケールを 2 か所に持たない）。 */}
        {currentCount !== undefined ? (
          <span className="tabular">このページ {formatCount(currentCount)} 件</span>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={!canGoPrev || loading}
        >
          <ChevronLeft aria-hidden />
          前へ
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={nextCursor === null || loading}
        >
          次へ
          <ChevronRight aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/**
 * カーソルのスタックを管理する補助フック。
 * 「前へ」を成立させるには辿ってきたカーソルを覚えておく必要があり、
 * これを画面ごとに書くと必ずどこかで戻れなくなるので共通化する。
 */
export function useCursorPagination(initialLimit = 50): {
  cursor: string | null;
  limit: number;
  canGoPrev: boolean;
  setLimit: (n: number) => void;
  goNext: (nextCursor: string | null) => void;
  goPrev: () => void;
  reset: () => void;
} {
  const [limit, setLimitState] = React.useState(initialLimit);
  // stack[0] は常に先頭ページ（cursor = null）。
  const [stack, setStack] = React.useState<Array<string | null>>([null]);

  const cursor = stack[stack.length - 1] ?? null;

  const goNext = React.useCallback((nextCursor: string | null) => {
    if (nextCursor === null) return;
    setStack((s) => [...s, nextCursor]);
  }, []);

  const goPrev = React.useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const reset = React.useCallback(() => setStack([null]), []);

  // 表示件数を変えるとカーソルの意味が変わるので、必ず先頭へ戻す。
  const setLimit = React.useCallback((n: number) => {
    setLimitState(n);
    setStack([null]);
  }, []);

  return { cursor, limit, canGoPrev: stack.length > 1, setLimit, goNext, goPrev, reset };
}
